import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { meetingsApi } from '@/services/api'
import { useActions } from '@/modules/action-management/hooks'
import { isOpen } from '@/modules/action-management/utils'
import { useNotificationsRead } from '@/store/notificationsRead'
import { formatTime } from '@/utils/format'
import type { AppNotification, Priority } from '@/types'

/** ActionTask priority → notification priority. */
const taskPriority = (p: string): Priority | undefined =>
  p === 'critical' ? 'urgent' : p === 'high' ? 'high' : undefined

/**
 * The notification feed is DERIVED live from real data (no stored feed):
 *   - task reminders that have come due (open tasks, reminderAt ≤ now)
 *   - meetings scheduled for today
 * Read/dismissed state is remembered per derived id in localStorage.
 */
export function useNotificationFeed() {
  const { data: tasks = [] } = useActions()
  const { data: meetings = [] } = useQuery({
    queryKey: ['meetings', 'notification-feed'],
    queryFn: () => meetingsApi.all(),
    staleTime: 60_000,
  })
  const read = useNotificationsRead((s) => s.read)

  return useMemo<AppNotification[]>(() => {
    const now = Date.now()
    const today = new Date().toDateString()
    const list: AppNotification[] = []

    // Task reminders that have come due.
    for (const t of tasks) {
      if (t.reminderAt && isOpen(t) && new Date(t.reminderAt).getTime() <= now) {
        list.push({
          // Include reminderAt so rescheduling a reminder re-surfaces it.
          id: `rem-${t.id}-${t.reminderAt}`,
          type: 'reminder',
          title: 'Task reminder',
          body: t.title,
          at: t.reminderAt,
          read: false,
          link: `/actions/${t.id}`,
          entity: { type: 'task', id: t.id, label: t.number },
          priority: taskPriority(t.priority),
        })
      }
    }

    // Meetings scheduled for today.
    for (const m of meetings) {
      if (m.status === 'scheduled' && new Date(m.start).toDateString() === today) {
        list.push({
          id: `mtg-${m.id}-${m.start}`,
          type: 'meeting',
          title: 'Meeting today',
          body: `${m.title} · ${formatTime(m.start)}`,
          at: m.start,
          read: false,
          link: `/meetings/${m.id}`,
          entity: { type: 'meeting', id: m.id, label: m.title },
        })
      }
    }

    const readSet = new Set(read)
    return list
      .map((n) => ({ ...n, read: readSet.has(n.id) }))
      .sort((a, b) => (a.at < b.at ? 1 : -1))
  }, [tasks, meetings, read])
}
